import React, { useEffect, useMemo, useState } from 'react';
import { useReducedMotion } from 'framer-motion';
import { BatteryMediumIcon, HomeIcon, PauseIcon, PlayIcon, SunIcon, UtilityPoleIcon } from 'lucide-react';
import type { Season, UsageProfile } from '../../types/scan';
import { simulateDay } from '../../utils/energy';
import { dec } from '../../utils/format';
const DEMO: Pick<UsageProfile, 'hasSolar' | 'solarPanels' | 'annualUsage' | 'contract'> = {
  hasSolar: true,
  solarPanels: 16,
  annualUsage: 5200,
  contract: 'dynamisch'
};
const STEP = 0.25;
interface FlowProps {
  d: string;
  active: boolean;
  color: string;
}
function Flow({
  d,
  active,
  color
}: FlowProps) {
  return <g data-id="element-78" data-mp="gigdd1chz8qd">
      <path d={d} stroke="rgba(245,243,236,0.12)" strokeWidth="2" fill="none" data-id="element-79" data-mp="kluu5u89ft4v" />
      {active && <path d={d} stroke={color} strokeWidth="2.5" strokeLinecap="round" fill="none" className="flow-line" data-id="element-80" data-mp="18bgvan870ifi" />}
    </g>;
}
interface NodeProps {
  x: string;
  y: string;
  icon: React.ReactNode;
  label: string;
  value: string;
  highlight?: boolean;
}
function Node({
  x,
  y,
  icon,
  label,
  value,
  highlight
}: NodeProps) {
  return <div className="absolute -translate-x-1/2 -translate-y-1/2 text-center" style={{
    left: x,
    top: y
  }} data-id="element-81" data-mp="1y3navx0feagw">
      <div className={`mx-auto flex h-14 w-14 items-center justify-center rounded-2xl transition-colors duration-200 sm:h-16 sm:w-16 ${highlight ? 'bg-volt text-ink' : 'bg-forest-light text-canvas'}`} data-id="element-82" data-mp="2cs1qz9murvfe">
        {icon}
      </div>
      <div className="mt-2 whitespace-nowrap text-[12px] text-canvas/55" data-id="element-83" data-mp="2hqv430y89kf1">{label}</div>
      <div className="whitespace-nowrap text-[15px] font-semibold tabular-nums text-canvas" data-id="element-84" data-mp="2ht7i530v7amt">{value}</div>
    </div>;
}
export function EnergyFlowDemo() {
  const reduce = useReducedMotion();
  const [season, setSeason] = useState<Season>('zomer');
  const [t, setT] = useState(13);
  const [playing, setPlaying] = useState(!reduce);
  const points = useMemo(() => simulateDay(DEMO, 10.2, 5, season, STEP), [season]);
  useEffect(() => {
    if (!playing) return;
    const id = window.setInterval(() => setT(v => v + STEP >= 24 ? 0 : v + STEP), 90);
    return () => window.clearInterval(id);
  }, [playing]);
  const p = points[Math.min(points.length - 1, Math.round(t / STEP))];
  const totals = useMemo(() => {
    const solar = points.reduce((s, x) => s + x.solar * STEP, 0);
    const fromBattery = points.reduce((s, x) => s + Math.max(-x.battery, 0) * STEP, 0);
    const fromGrid = points.reduce((s, x) => s + Math.max(x.grid, 0) * STEP, 0);
    const load = points.reduce((s, x) => s + x.load * STEP, 0);
    return {
      solar,
      fromBattery,
      fromGrid,
      independence: load > 0 ? Math.round((load - fromGrid) / load * 100) : 0
    };
  }, [points]);
  const charging = p.battery > 0.05;
  const discharging = p.battery < -0.05;
  const importing = p.grid > 0.05;
  const exporting = p.grid < -0.05;
  return <div className="rounded-[28px] bg-ink p-5 text-canvas sm:p-7" data-id="element-85" data-mp="1xwdgm62pmqr">
      <div className="flex flex-wrap items-center justify-between gap-4" data-id="element-86" data-mp="6zbzohjzlzaq">
        <div data-id="element-87" data-mp="h30siohxqqzv">
          <div className="text-[13px] text-canvas/55" data-id="element-88" data-mp="h3k29hxfm92t">Voorbeeld · 2-onder-1-kap, 16 panelen, Kluis 10</div>
          <div className="mt-1 text-4xl font-semibold tabular-nums tracking-tight" data-id="element-89" data-mp="c231rniv0omr">{p.label}</div>
        </div>
        <div className="flex items-center gap-2" data-id="element-90" data-mp="74uuwj35w70t">
          <div role="radiogroup" aria-label="Seizoen" className="flex rounded-full bg-forest p-1" data-id="element-91" data-mp="33hljz0v1lp5">
            {(['zomer', 'winter'] as Season[]).map(s => <button key={s} role="radio" aria-checked={season === s} onClick={() => setSeason(s)} className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium capitalize transition-colors duration-150 ${season === s ? 'bg-canvas text-ink' : 'text-canvas/70 hover:text-canvas'}`} data-id="element-92" data-mp="zvcxlizz46dp">
                {s}dag
              </button>)}
          </div>
          <button type="button" onClick={() => setPlaying(v => !v)} aria-label={playing ? 'Pauzeer simulatie' : 'Speel simulatie af'} className="flex h-10 w-10 items-center justify-center rounded-full bg-volt text-ink transition-colors duration-150 hover:bg-volt-deep" data-id="element-93" data-mp="1dr6suz7tqx8c">
            {playing ? <PauseIcon className="h-4 w-4" data-id="element-94" data-mp="tb5emmkn4fyn" /> : <PlayIcon className="h-4 w-4" data-id="element-95" data-mp="tc71dob0qqw7" />}
          </button>
        </div>
      </div>

      <div className="relative mt-6 aspect-[16/11] w-full sm:aspect-[16/10]" data-id="element-96" data-mp="1h21co1fow7ec">
        <svg viewBox="0 0 400 250" preserveAspectRatio="none" className="absolute inset-0 h-full w-full" aria-hidden="true" data-id="element-97" data-mp="1lzjbv03wn895">
          <Flow d="M64 50 L200 125" active={p.solar > 0.05} color="#F2B33D" data-id="element-98" data-mp="188o9m6nwvpej" />
          <Flow d={importing ? 'M64 200 L200 125' : 'M200 125 L64 200'} active={importing || exporting} color="#7A93E8" data-id="element-99" data-mp="1c9q306v5d78l" />
          <Flow d={discharging ? 'M336 125 L200 125' : 'M200 125 L336 125'} active={charging || discharging} color="#D2F36B" data-id="element-100" data-mp="1zyspbs0q7o9f" />
        </svg>
        <Node x="16%" y="20%" icon={<SunIcon className="h-6 w-6" data-id="element-102" data-mp="3pgpqs8gwpkqs" />} label="Zonnepanelen" value={`${dec(p.solar)} kW`} highlight={p.solar > 0.05} data-id="element-101" data-mp="3ph7svlq92t6m" />
        <Node x="16%" y="80%" icon={<UtilityPoleIcon className="h-6 w-6" data-id="element-104" data-mp="31n2nicfqnhd" />} label={exporting ? 'Teruglevering' : 'Stroomnet'} value={`${dec(Math.abs(p.grid))} kW`} data-id="element-103" data-mp="3khmq107ym0om" />
        <Node x="50%" y="50%" icon={<HomeIcon className="h-6 w-6" data-id="element-106" data-mp="36idcy9f1vmov" />} label="Verbruik huis" value={`${dec(p.load)} kW`} data-id="element-105" data-mp="3uaanzyaoeaox" />
        <Node x="84%" y="50%" icon={<BatteryMediumIcon className="h-6 w-6" data-id="element-108" data-mp="31l3g48pc08r0" />} label={charging ? 'Laadt op' : discharging ? 'Levert stroom' : 'Stand-by'} value={`${p.soc}%`} highlight={charging || discharging} data-id="element-107" data-mp="31jk1emjbq5rc" />
      </div>

      <label htmlFor="flow-time" className="sr-only" data-id="element-109" data-mp="vqk8glzgmrt8">Tijdstip van de dag</label>
      <input id="flow-time" type="range" min={0} max={24 - STEP} step={STEP} value={t} onChange={e => {
      setPlaying(false);
      setT(Number(e.target.value));
    }} className="range mt-4 w-full" data-id="element-110" data-mp="vsono02t7d8t" />
      <div className="mt-1 flex justify-between text-[12px] text-canvas/40" data-id="element-111" data-mp="12s7dbrwclb3i">
        <span data-id="element-112" data-mp="17twkuxkd36mu">00:00</span>
        <span data-id="element-113" data-mp="17tmuh8vemxgm">06:00</span>
        <span data-id="element-114" data-mp="1cry5hmxfre0f">12:00</span>
        <span data-id="element-115" data-mp="1cshluz7ftucc">18:00</span>
        <span data-id="element-116" data-mp="1cqn57x378oe2">24:00</span>
      </div>

      <dl className="mt-6 grid grid-cols-2 gap-y-4 border-t border-canvas/10 pt-5 sm:grid-cols-4" data-id="element-117" data-mp="1cplpvgf5hfqj">
        <div data-id="element-118" data-mp="f2czp847fii7">
          <dt className="text-[13px] text-canvas/55" data-id="element-119" data-mp="k30zojaev9ok">Zelfvoorzienend</dt>
          <dd className="text-2xl font-semibold tabular-nums text-volt" data-id="element-120" data-mp="k16gb9esfpml">{totals.independence}%</dd>
        </div>
        <div data-id="element-121" data-mp="p0j2drxu4so4">
          <dt className="text-[13px] text-canvas/55" data-id="element-122" data-mp="tsu14rgua8f3">Zon opgewekt</dt>
          <dd className="text-lg font-medium tabular-nums" data-id="element-123" data-mp="tv6rnt0nxr3r">{dec(totals.solar)} kWh</dd>
        </div>
        <div data-id="element-124" data-mp="3sgzuhhlncp0h">
          <dt className="text-[13px] text-canvas/55" data-id="element-125" data-mp="1ct2ehte7dga">Uit batterij</dt>
          <dd className="text-lg font-medium tabular-nums" data-id="element-126" data-mp="1ayh32e7ljbo">{dec(totals.fromBattery)} kWh</dd>
        </div>
        <div data-id="element-127" data-mp="2ekyilaeox1hm">
          <dt className="text-[13px] text-canvas/55" data-id="element-128" data-mp="2ekq4xgaqts5d">Van het net</dt>
          <dd className="text-lg font-medium tabular-nums" data-id="element-129" data-mp="2alz4p0azdue1">{dec(totals.fromGrid)} kWh</dd>
        </div>
      </dl>
    </div>;
}